import {
  canCompleteQuotingAddEmployees,
  getRequiredSubInfo,
  getSubscribersPageForGroup,
} from 'api/operations/electionService';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { createContext, useContext } from 'react';
import { FiltersObject } from 'store/general/paginationDelegate';
import IAddEmployeesPage from 'store/modules/add-employees/iAddEmployeesPage';
import {
  RequiredSubInfo,
  RequiredSubInfoPresenter,
  SubscriberAddEmployeesProgress,
  SubscribersFilter,
} from 'types/electionService';
import { quoteChecklistEmployeeActions } from 'store/pages/quote/add-employees/quoteChecklistEmployeeActions';
import { getStandardActionsWithSelfEnrollFeature } from 'store/modules/add-employees/inviteEmployeeActions';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { USER_ACTIONS } from 'types/enrollmentService';
import { applyProposalUserAction } from 'api/operations/enrollmentService';
import QuoteFlowDelegate, { QuoteFlowDomainParams } from 'store/flows/quote/quoteFlowDelegate';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import { createRequiredSubInfoPresenter } from 'global/utils/election/electionPresenters';
import { getRootStoreInstance } from 'store/rootStore';

export interface QuoteAddEmployeesFilters extends FiltersObject {
  employeeName: string;
  subProgress: SubscriberAddEmployeesProgress | null;
}

export default class QuoteAddEmployeesPageStore extends IAddEmployeesPage<QuoteAddEmployeesFilters> {
  private _rootStore = getRootStoreInstance();
  public readonly flowDelegate: QuoteFlowDelegate;

  protected _domainParams: QuoteFlowDomainParams;
  private _requiredSubInfo: RequiredSubInfo | null = null;

  public get requiredSubInfoPresenter(): RequiredSubInfoPresenter | null {
    return createRequiredSubInfoPresenter(toJS(this._requiredSubInfo), 'quoting');
  }

  // No validation logic is currently required for this page.
  // This is an optional functionality.
  // eslint-disable-next-line class-methods-use-this
  public get stepCanComplete(): boolean {
    return true;
  }

  async getRequiredSubInfo(): Promise<void> {
    this._setIsLoading(true);
    const requiredSubInfo = await getRequiredSubInfo(this._rootStore.domainStoreV2.domainHeader.id);
    runInAction(() => {
      this._requiredSubInfo = requiredSubInfo;
      this._setIsLoading(false);
    });
  }

  public async getEmployees(): Promise<void> {
    this._setIsLoading(true);
    const { result, totalElements, totalPages } = await getSubscribersPageForGroup({
      groupId: this._domainParams.groupId,
      sortBy: this.paginationDelegate.sortBy,
      sortOrder: this.paginationDelegate.sortOrder,
      status: [],
      addOnMethod: [],
      progress: this.paginationDelegate.filters.subProgress ? [this.paginationDelegate.filters.subProgress] : [],
      filter: SubscribersFilter.ALL,
      name: this.paginationDelegate.filters.employeeName,
      page: this.paginationDelegate.page,
    });
    runInAction(() => {
      this._createEmployeeDelegates(result);
      this.paginationDelegate.setTotalElements(totalElements);
      this.paginationDelegate.setTotalPages(totalPages);
      this._setIsLoading(false);
    });
  }

  public async onComplete(): Promise<void> {
    this._setIsLoading(true);
    const canComplete = await canCompleteQuotingAddEmployees(this._domainParams.groupId);

    if (canComplete) {
      await applyProposalUserAction(
        this._domainParams.periodId,
        this._domainParams.cartId,
        USER_ACTIONS.COMPLETE_ADD_EMPLOYEES,
      );
      runInAction(() => {
        this._setIsLoading(false);
        this.flowDelegate.onCompleteRedirect();
      });
    } else {
      this._setIsLoading(false);
      toastManager.addToast('Some employees have incomplete data! You cannot proceed now.', ToastColorTypes.WARNING);
    }
  }

  constructor(params: QuoteFlowDomainParams) {
    super(
      { status: null, employeeName: '', subProgress: null, employeeClass: null },
      getStandardActionsWithSelfEnrollFeature(quoteChecklistEmployeeActions),
      params,
    );
    this._domainParams = params;
    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: params,
      nextPageId: QuoteRouteV2Id.QUOTE_CHECKLIST,
    });

    makeObservable<QuoteAddEmployeesPageStore, '_domainParams' | '_requiredSubInfo'>(this, {
      _domainParams: observable,
      _requiredSubInfo: observable,
      stepCanComplete: computed,
      requiredSubInfoPresenter: computed,
      getEmployees: action.bound,
      onComplete: action.bound,
      getRequiredSubInfo: action.bound,
    });
  }
}

const QuoteAddEmployeesPageStoreContext = createContext(new QuoteAddEmployeesPageStore({} as QuoteFlowDomainParams));

export const useQuoteAddEmployeesPageStore = (): QuoteAddEmployeesPageStore => {
  return useContext(QuoteAddEmployeesPageStoreContext);
};

export { QuoteAddEmployeesPageStore, QuoteAddEmployeesPageStoreContext };
